import { useEffect, useState } from "react";
import { routeFrom } from "./viewer/GridViewer";
import { useI18n } from "./i18n";
import PurchasePanel from "./purchase/PurchasePanel";
import type { SelectionMode } from "./viewer/rect-selection";
import type { GridSelection } from "./lib/grid-selection";
import type { OccupancyRect } from "./lib/selection-occupancy";
import type { ViewerContextState } from "./viewer/context-state";

import { AppShell } from "./ui/AppShell";
import { TopNavigation } from "./ui/TopNavigation";
import { GridWorkspace } from "./ui/GridWorkspace";
import { SideDrawer } from "./ui/SideDrawer";
import { ParcelProfilePanel } from "./ui/ParcelProfilePanel";
import { BottomSheet } from "./ui/BottomSheet";
import { DetailPanelContent } from "./ui/DetailPanelContent";
import { ArtworkProfilePanel } from "./ui/artwork/ArtworkProfilePanel";
import { ARTWORK_SELECT_EVENT, ARTWORK_DESELECT_EVENT } from "./viewer/GridArtworkLayer";
import { AuthRoute } from "./ui/AuthRoute";
import { DashboardRoute } from "./ui/dashboard/DashboardRoute";
import { AdminConsoleRoute } from "./ui/admin/AdminConsoleRoute";
import { ArtistProfileRoute } from "./ui/artist/ArtistProfileRoute";
import { CanvasEditorRoute } from "./ui/canvas/CanvasEditorRoute";
import { CanvasErrorBoundary } from "./ui/canvas/CanvasErrorBoundary";
import { VoxelEditorRoute } from "./ui/voxel/VoxelEditorRoute";
import { FrozenArtworkRoute } from "./ui/artwork/FrozenArtworkRoute";
import { NarrativePage } from "./ui/NarrativePage";
import { deriveGridContext, gridContextIdentity } from "./ui/grid-context";
import { useIsDesktop } from "./ui/media";
import { useHashRoute, viewFromRoute, canvasIdentityFromRoute, voxelIdentityFromRoute, publicPublicationFromRoute, artworkIdFromRoute, creatorFromRoute, type PrimaryView } from "./ui/routing";
import { PublicPublicationView } from "./ui/PublicPublicationView";
import { AboutPage } from "./ui/public/AboutPage";
import { HowItWorksPage } from "./ui/public/HowItWorksPage";
import { ExplorePage } from "./ui/public/ExplorePage";
import { CreatorsPage } from "./ui/public/CreatorsPage";
import { RentPage } from "./ui/public/RentPage";
import { LegalPage, legalDocFromRoute } from "./ui/public/LegalPage";
import { OnboardingRoute } from "./ui/onboarding/OnboardingRoute";
import { MarketplaceRoute } from "./ui/marketplace/MarketplaceRoute";
import { FeedsRoute } from "./ui/social/FeedsRoute";
import { CreatorProfileRoute } from "./ui/social/CreatorProfileRoute";
import { BackToMarketplace } from "./ui/marketplace/BackToMarketplace";
import { useZoneClassification } from "./ui/useZoneClassification";
import { SaleAuthorityProvider } from "./ui/SaleAuthorityProvider";
import { useZoneManifest, useOverlayRenderable } from "./ui/useZoneManifest";
import { useWalletSessionSync } from "./ui/useWalletSessionSync";

export default function App() {
  useWalletSessionSync();
  const route = useHashRoute();
  const view: PrimaryView = viewFromRoute(route);

  // WO#5 §NARRATIVE — the #auth route keeps the legacy fullscreen
  // shell (dedicated auth surface). All other views live inside the
  // vertically scrolling narrative page.
  if (view === "auth") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="fullscreen"
          topBar={<TopNavigation activeView={view} narrative={false} />}
        >
          <AuthRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Territory Canvas V1 — private management editor (#canvas/<identity_id>).
  if (view === "canvas") {
    const identityId = canvasIdentityFromRoute(route);
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="fullscreen"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <CanvasErrorBoundary>
            <CanvasEditorRoute identityId={identityId} />
          </CanvasErrorBoundary>
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #26 · XYZ Voxel Editor — private 3D management editor (#voxel/<identity_id>).
  if (view === "voxel_editor") {
    const identityId = voxelIdentityFromRoute(route);
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="fullscreen"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <VoxelEditorRoute identityId={identityId} />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  if (view === "publication_public") {
    const identityId = publicPublicationFromRoute(route);
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="fullscreen"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <PublicPublicationView identityId={identityId} />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Frozen Artwork detail (#artwork/<id>) — read-only #14 Phase C surface.
  if (view === "artwork_public") {
    const aid = artworkIdFromRoute(route);
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <FrozenArtworkRoute artworkId={aid} />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Artist System #13 / Phase C — Profile / Collection surface (#profile).
  if (view === "profile") {    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <ArtistProfileRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #26 · Increment 3 — dedicated PUBLIC information pages. Each is a real
  // route rendered in the scroll shell (NOT a section spliced into the frozen
  // Home). The shared top navigation is reused as-is; Home stays untouched.
  if (view === "about" || view === "how_it_works" || view === "explore" || view === "creators" || view === "rent") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView={view} narrative={false} />}
        >
          {view === "about" && <AboutPage />}
          {view === "how_it_works" && <HowItWorksPage />}
          {view === "explore" && <ExplorePage />}
          {view === "creators" && <CreatorsPage />}
          {view === "rent" && <RentPage />}
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #33 · W7 — DRAFT legal / privacy surfaces (#legal, #legal/<doc>).
  // Rendered in the scroll shell reusing the existing public page design
  // system (no redesign). Additive: Home and all other routes are untouched.
  if (view === "legal") {
    const doc = legalDocFromRoute(route);
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <LegalPage doc={doc} />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #29 · Stage 3 — LAND + Artworks Marketplace (#marketplace), scroll shell.
  if (view === "marketplace") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="marketplace" narrative={false} />}
        >
          <MarketplaceRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #30 · Social Layer V1 — feeds (#feed) + public creator profile (#u/<wallet>).
  if (view === "feed") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="feed" narrative={false} />}
        >
          <FeedsRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  if (view === "creator_profile") {
    const creator = creatorFromRoute(route);
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <CreatorProfileRoute wallet={creator} />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #27 — guided onboarding CONTROL CENTER (#onboarding), scroll shell.
  if (view === "onboarding") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <OnboardingRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // WO#3 — additive #dashboard owner-management surface (fullscreen shell).
  if (view === "dashboard") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <DashboardRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  // Macro #20 — Admin "Command Console" (#admin). Presence is capability-gated;
  // authority is server-enforced (require_admin). Direct visits fail closed.
  if (view === "admin") {
    return (
      <SaleAuthorityProvider>
        <AppShell
          variant="scroll"
          topBar={<TopNavigation activeView="grid" narrative={false} />}
        >
          <AdminConsoleRoute />
        </AppShell>
      </SaleAuthorityProvider>
    );
  }

  return (
    <SaleAuthorityProvider>
      <AppShell
        variant="narrative"
        topBar={<TopNavigation activeView={view} narrative={true} />}
      >
        <NarrativePage view={view} gridSlot={<GridExplorer view={view} />} />
      </AppShell>
    </SaleAuthorityProvider>
  );
}

/**
 * GridExplorer — mounts the shared Grid workspace inside the
 * narrative Grid section (WO#5 §02).
 *
 * Behaviour preserved from pre-WO#5:
 *   1. `GridViewer` PUSHES `ViewerContextState` through
 *      `onContextStateChange`.
 *   2. Committed rectangle flows through `onCommitSelection`.
 *   3. Purchase route composition remains a shell concern (see
 *      `ui/grid-context.ts`).
 *   4. Desktop → ContextPanel (absolute right, inside the section
 *      container). Mobile → BottomSheet auto-opened on parcel /
 *      purchase context.
 */
function GridExplorer({ view }: { view: PrimaryView }) {
  const { t } = useI18n();
  const purchase = view === "purchase";
  const [mode, setMode] = useState<SelectionMode>(
    purchase ? "select" : "browse",
  );
  const [selection, setSelection] = useState<GridSelection | null>(null);
  // Macro #21 §5 — canonical occupancy footprint (indexer-sourced, already
  // loaded by the viewer) forwarded to PurchasePanel for the fail-fast guard.
  const [occupancy, setOccupancy] = useState<OccupancyRect[]>([]);
  // HOTFIX purchase-indexed-grid-refresh — monotonic nonce bumped when a
  // purchase is authoritatively indexed; forwarded to GridWorkspace/GridViewer
  // so the shared Grid reloads the new parcel's tiles without a manual refresh.
  const [gridRefreshNonce, setGridRefreshNonce] = useState(0);
  // WO-VRD-6 §SELECT-CLEAR — returning to Inspect (browse) must also drop
  // the committed rectangle's Context info, mirroring the canvas clear in
  // GridViewer. Without this the right panel keeps showing the stale
  // purchase preview after the rectangle has visually disappeared.
  const handleModeChange = (m: SelectionMode) => {
    setMode(m);
    if (m === "browse") setSelection(null);
  };
  const [viewerContext, setViewerContext] = useState<ViewerContextState>({
    kind: "empty",
    chainMismatch: false,
    hasAnySnapshot: false,
    refreshError: null,
  });
  const isDesktop = useIsDesktop();

  // #15 discoverability — a Frozen Artwork selected on the Grid surfaces the
  // Artwork Profile panel (backend-authoritative) instead of the Parcel Profile.
  const [selectedArtworkId, setSelectedArtworkId] = useState<string | null>(null);
  useEffect(() => {
    const onSelect = (e: Event) => {
      const id = (e as CustomEvent).detail?.artworkId;
      if (typeof id === "string") { setSelectedArtworkId(id); setRightDismissed(false); }
    };
    const onDeselect = () => setSelectedArtworkId(null);
    window.addEventListener(ARTWORK_SELECT_EVENT, onSelect);
    window.addEventListener(ARTWORK_DESELECT_EVENT, onDeselect);
    return () => {
      window.removeEventListener(ARTWORK_SELECT_EVENT, onSelect);
      window.removeEventListener(ARTWORK_DESELECT_EVENT, onDeselect);
    };
  }, []);
  const clearArtwork = () => {
    setSelectedArtworkId(null);
    window.dispatchEvent(new Event(ARTWORK_DESELECT_EVENT));
  };

  const gridContext = deriveGridContext({
    view,
    viewer: viewerContext,
    committedSelection: selection,
  });

  const parcelZoneInput =
    !purchase && gridContext.kind === "parcel"
      ? {
          x: gridContext.detail.parcel.x,
          y: gridContext.detail.parcel.y,
          w: gridContext.detail.parcel.w,
          h: gridContext.detail.parcel.h,
        }
      : null;
  const parcelZoneInfo = useZoneClassification(parcelZoneInput);

  const zoneManifestState = useZoneManifest();
  const zoneOverlay = useOverlayRenderable(zoneManifestState);

  const [sheetDismissed, setSheetDismissed] = useState(false);
  // WO-VRD-30 — desktop side drawers open on action and can be dismissed.
  const [rightDismissed, setRightDismissed] = useState(false);
  const [leftDismissed, setLeftDismissed] = useState(false);
  const identityKey = gridContextIdentity(gridContext);
  useEffect(() => {
    if (identityKey != null) setSheetDismissed(false);
  }, [identityKey]);
  useEffect(() => {
    if (identityKey != null) {
      setRightDismissed(false);
      setLeftDismissed(false);
      setSelectedArtworkId(null);
    }
  }, [identityKey]);
  const shouldAutoOpenSheet =
    gridContext.kind === "parcel" || gridContext.kind === "purchase";
  const sheetOpen = shouldAutoOpenSheet && !sheetDismissed;

  const contextPanelTitle =
    selectedArtworkId ? t("grid.panel.titleArtwork") : gridContext.kind === "purchase" ? t("grid.panel.titleSelection") : t("grid.panel.titleContext");
  const contextPanelSubtitle =
    gridContext.kind === "purchase" && selection
      ? t("grid.panel.selectionSize", { w: selection.w, h: selection.h, slots: selection.w * selection.h })
      : purchase
        ? t("grid.panel.drawRectHint")
        : undefined;

  const renderPanelChildren = () => {
    if (selectedArtworkId) {
      const label = viewerContext.kind === "parcel" ? t("grid.panel.parcelNum", { id: viewerContext.detail.parcel.id }) : t("grid.panel.parcel");
      return <ArtworkProfilePanel artworkId={selectedArtworkId} parcelLabel={label} onBackToParcel={clearArtwork} />;
    }
    if (gridContext.kind === "purchase") {
      return (
        <PurchasePanel
          selection={gridContext.selection}
          occupancy={occupancy}
          onClearSelection={() => setSelection(null)}
          onPurchaseIndexed={() => setGridRefreshNonce((n) => n + 1)}
        />
      );
    }
    if (purchase) {
      return (
        <PurchasePanel
          selection={null}
          occupancy={occupancy}
          onClearSelection={() => setSelection(null)}
          onPurchaseIndexed={() => setGridRefreshNonce((n) => n + 1)}
        />
      );
    }
    return <DetailPanelContent context={viewerContext} zoneInfo={parcelZoneInfo} />;
  };

  // MACRO #31 — mobile BottomSheet parity: a parcel tap must surface the FULL
  // Parcel Profile dossier (ownership, Sell Land, artworks, capacity, owner
  // moderation) PLUS the technical detail — i.e. desktop's LEFT + RIGHT drawers
  // combined into one scrollable sheet. Artwork / purchase keep their dedicated
  // panels (identical to desktop).
  const renderMobileSheetChildren = () => {
    if (selectedArtworkId || purchase || gridContext.kind === "purchase") {
      return renderPanelChildren();
    }
    if (parcelSelected) {
      return (
        <div className="space-y-4" data-testid="mobile-territory-sheet">
          <ParcelProfilePanel bare context={viewerContext} zoneInfo={parcelZoneInfo} />
          <DetailPanelContent context={viewerContext} zoneInfo={parcelZoneInfo} />
        </div>
      );
    }
    return renderPanelChildren();
  };

  // WO-VRD-30 — drawer open logic. RIGHT = Context/Selection (opens when
  // an area is drawn / a purchase selection is active). LEFT = Parcel
  // Profile (opens when a parcel is clicked). Both stay MOUNTED per their
  // gate so e2e testids resolve; `open` only drives the slide.
  const parcelSelected = viewerContext.kind === "parcel";
  // WO-VRD-31 (Claudio) — a rectangle drawn ENTIRELY outside the 1000×1000
  // grid must NOT surface the Context drawer (mirror Inspect clicking the
  // void). The committed geometry still flows unchanged through
  // onCommitSelection; this only gates the drawer/sheet visibility.
  const selectionInsideGrid =
    selection != null &&
    selection.x < 1000 &&
    selection.y < 1000 &&
    selection.x + selection.w > 0 &&
    selection.y + selection.h > 0;
  // WO-VRD-32 (Claudio) — an "outside grid" browse tap (Inspect
  // void-click) must DISMISS the Context drawer, mirroring the
  // select-mode void-tap. It surfaces as a selection→cell→outside
  // viewer context; gate it out so the drawer closes.
  const viewerOutside =
    viewerContext.kind === "selection" &&
    viewerContext.selection.kind === "cell" &&
    viewerContext.selection.cell.kind === "outside";
  void viewerOutside;
  // WO-VRD-32 (Claudio) — the committed rectangle opens the
  // Context/Selection drawer GLOBALLY (not only on #purchase) and
  // INDEPENDENTLY of whether the user toggled Inspect first. Previously
  // the non-purchase branch keyed off `viewerContext` alone, so a plain
  // Select draw surfaced nothing unless Inspect had already populated
  // the context. Non-purchase still keeps the browse-context drawer.
  const rightShouldShow = purchase
    ? gridContext.kind === "purchase" && selectionInsideGrid
    : selectedArtworkId != null ||
      (gridContext.kind === "purchase" && selectionInsideGrid);
  const rightOpen = rightShouldShow && !rightDismissed;
  const leftOpen = parcelSelected && !leftDismissed;

  return (
    <>
      <BackToMarketplace />
      <GridWorkspace
        mode={mode}
        onModeChange={handleModeChange}
        onCommitSelection={setSelection}
        onClearSelection={() => setSelection(null)}
        onContextStateChange={setViewerContext}
        onOccupancyChange={setOccupancy}
        zoneOverlay={zoneOverlay}
        zoneSettled={zoneManifestState.status !== "loading"}
        zonesUnavailable={zoneManifestState.status === "unavailable"}
        onRetryZones={zoneManifestState.retry}
        refreshSignal={gridRefreshNonce}
        hideMobileChrome={!isDesktop && sheetOpen}
      />
      {/* WO-VRD-30 §LEFT-DRAWER — Parcel Profile dossier, full page-3
          height. Mounted only while a real parcel context is active so
          the `parcel-profile` testid is absent otherwise (e2e invariant).
          Mobile keeps the BottomSheet flow below. */}
      {isDesktop && parcelSelected && (
        <SideDrawer
          side="left"
          open={leftOpen}
          title={t("grid.panel.parcelProfileTitle")}
          testId="parcel-profile-drawer"
          onClose={() => setLeftDismissed(true)}
        >
          <ParcelProfilePanel
            bare
            context={viewerContext}
            zoneInfo={parcelZoneInfo}
          />
        </SideDrawer>
      )}
      {/* WO-VRD-30 §RIGHT-DRAWER — Context / Selection, full page-3
          height. Kept mounted on desktop so `context-panel-purchase`
          resolves; `open` drives the slide. */}
      {isDesktop && (
        <SideDrawer
          side="right"
          open={rightOpen}
          title={contextPanelTitle}
          subtitle={contextPanelSubtitle}
          testId={
            purchase ? "context-panel-purchase" : "context-panel-detail"
          }
          onClose={() => {
            // #15 — closing the Artwork card must FULLY reset the artwork
            // selection + any in-flight placement edit, so re-clicking the
            // same piece re-opens the card (and it's no longer in drag mode).
            if (selectedArtworkId) clearArtwork();
            else setRightDismissed(true);
          }}
        >
          {renderPanelChildren()}
        </SideDrawer>
      )}
      {!isDesktop && (
        <BottomSheet
          open={sheetOpen}
          testId={
            purchase ? "bottom-sheet-purchase" : "bottom-sheet-detail"
          }
          onCollapse={() => setSheetDismissed(true)}
        >
          {renderMobileSheetChildren()}
        </BottomSheet>
      )}
    </>
  );
}

export { routeFrom };
